import { useState } from 'react'
import QRCode from 'qrcode'
import { Loader2, ShieldCheck } from 'lucide-react'
import dosPasosService from '@/services/dosPasosService'
import { mensajeDeError } from '@/lib/errores'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Activar o verificar la 2FA de la cuenta (lo comparten el diálogo que abre el interceptor y la
 * pantalla Seguridad):
 *   - sin activar → QR + clave, código de confirmación y los códigos de respaldo (UNA vez);
 *   - activada → el código de 6 dígitos de la app, o uno de respaldo.
 *
 * `alVerificar(sesion2fa)` llega en cuanto la API emite la sesión, para guardarla aunque el asesor
 * cierre sin pulsar «Ya guardé los códigos»; `alTerminar()` cuando el flujo acaba.
 */
export default function FlujoDosPasos({ activado, alVerificar, alTerminar, explicacion }) {
  const [enrolamiento, setEnrolamiento] = useState(null) // { secreto, qr }
  const [codigo, setCodigo] = useState('')
  const [respaldo, setRespaldo] = useState(null)
  const [error, setError] = useState(null)
  const [enviando, setEnviando] = useState(false)

  const iniciar = async () => {
    setEnviando(true)
    setError(null)
    try {
      const d = await dosPasosService.iniciar()
      const qr = await QRCode.toDataURL(d.otpauthUrl, { margin: 1, width: 220 })
      setEnrolamiento({ secreto: d.secreto, qr })
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo iniciar la activación'))
    } finally {
      setEnviando(false)
    }
  }

  const enviar = async (evento) => {
    evento.preventDefault()
    const limpio = codigo.trim()
    if (!activado && !/^\d{6}$/.test(limpio)) {
      setError('El código debe tener 6 dígitos.')
      return
    }
    setEnviando(true)
    setError(null)
    try {
      if (activado) {
        const d = await dosPasosService.verificar(limpio)
        alVerificar(d.sesion2fa)
        alTerminar()
      } else {
        const d = await dosPasosService.confirmar(limpio)
        alVerificar(d.sesion2fa)
        setRespaldo(d.codigosRespaldo || [])
      }
      setCodigo('')
    } catch (e) {
      setError(mensajeDeError(e, 'Código inválido'))
    } finally {
      setEnviando(false)
    }
  }

  if (respaldo) {
    return (
      <div className="space-y-4">
        <Alert variant="success">
          <ShieldCheck size={16} />
          <AlertDescription>Verificación en dos pasos activada.</AlertDescription>
        </Alert>
        <Alert variant="warning">
          <AlertDescription className="space-y-2">
            <p>
              <strong>Guarda estos códigos de respaldo en un lugar seguro.</strong> Se muestran UNA sola
              vez y cada uno sirve para entrar una vez si pierdes el teléfono.
            </p>
            <div
              data-testid="codigos-respaldo"
              className="grid grid-cols-1 xs:grid-cols-2 gap-2 font-mono text-body-sm bg-card p-3 rounded-md"
            >
              {respaldo.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
        <Button onClick={alTerminar} className="w-full">
          Ya guardé los códigos
        </Button>
      </div>
    )
  }

  if (!activado && !enrolamiento) {
    return (
      <div className="space-y-4">
        <p className="text-body-sm text-neutral-600 dark:text-neutral-300">
          {explicacion ||
            'Necesitas una app autenticadora en el teléfono (Google Authenticator, Microsoft Authenticator, 1Password…). Solo se configura una vez.'}
        </p>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button onClick={iniciar} disabled={enviando} className="w-full">
          {enviando && <Loader2 size={16} className="animate-spin" />}
          Activar verificación en dos pasos
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={enviar} className="space-y-4">
      {enrolamiento ? (
        <>
          <p className="text-body-sm text-neutral-700 dark:text-neutral-300">
            1. Escanea este código con tu app autenticadora.
          </p>
          <img
            src={enrolamiento.qr}
            alt="Código QR de la verificación en dos pasos"
            className="mx-auto h-[220px] w-[220px] rounded-md bg-white p-1" /* tema-ok: el QR se escanea sobre blanco */
          />
          <p className="text-caption text-neutral-500 dark:text-neutral-400 break-all">
            ¿No puedes escanearlo? Escribe esta clave en la app:{' '}
            <code className="font-mono text-neutral-900 dark:text-neutral-50">{enrolamiento.secreto}</code>
          </p>
          <p className="text-body-sm text-neutral-700 dark:text-neutral-300">
            2. Escribe el código de 6 dígitos que muestra la app.
          </p>
        </>
      ) : (
        <p className="text-body-sm text-neutral-700 dark:text-neutral-300">
          {explicacion || 'Escribe el código de 6 dígitos de tu app autenticadora (o un código de respaldo).'}
        </p>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="codigo-dos-pasos">Código</Label>
        <Input
          id="codigo-dos-pasos"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={32}
          placeholder="123456"
          autoFocus
        />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" disabled={enviando || !codigo.trim()} className="w-full">
        {enviando && <Loader2 size={16} className="animate-spin" />}
        {enrolamiento ? 'Confirmar y activar' : 'Verificar'}
      </Button>
    </form>
  )
}
